import { getDeviceGps, getDeviceType } from './channelMapUtils';

export const VERDICT_COLORS = {
  reduce: '#ef5350',
  raise: '#fb8c00',
  ok: '#66bb6a',
  missing_data: '#bdbdbd',
  suppressed: '#90a4ae',
};

const VERDICT_LABELS = {
  reduce: 'Over-powered',
  raise: 'Raise needed',
  ok: 'OK',
  missing_data: 'Missing data',
  suppressed: 'Suppressed',
};

export function getVerdictColor(link) {
  if (link.staVerdict === 'reduce' || link.apVerdict === 'reduce') return VERDICT_COLORS.reduce;
  if (link.staVerdict === 'raise' || link.apVerdict === 'raise') return VERDICT_COLORS.raise;
  if (link.staVerdict === 'suppressed' || link.apVerdict === 'suppressed') return VERDICT_COLORS.suppressed;
  if (link.staVerdict === 'ok' && link.apVerdict === 'ok') return VERDICT_COLORS.ok;
  return VERDICT_COLORS.missing_data;
}

export function getVerdictLabel(verdict) {
  return VERDICT_LABELS[verdict] || verdict || '';
}

export function normalizeSerial(serial) {
  return String(serial || '').toLowerCase().replace(/:/g, '').trim();
}

function resolveDeviceGps(device, devicesBySerial) {
  const direct = getDeviceGps(device);
  if (direct) return direct;

  const deviceType = getDeviceType(device?.capabilities);
  if (deviceType !== 'AI' || !device?.parent_serial) return null;

  const parent = devicesBySerial.get(normalizeSerial(device.parent_serial));
  if (parent?.compatible !== 'jnet2') return null;
  return getDeviceGps(parent);
}

export function extractDeviceGPS(devices) {
  const gpsMap = new Map();
  if (!Array.isArray(devices)) return gpsMap;

  const devicesBySerial = new Map();
  devices.forEach((device) => {
    if (device?.serial_number) {
      devicesBySerial.set(normalizeSerial(device.serial_number), device);
    }
  });

  devices.forEach((device) => {
    const serial = device?.serial_number;
    if (!serial) return;

    const gps = resolveDeviceGps(device, devicesBySerial);
    if (!gps) return;

    gpsMap.set(normalizeSerial(serial), {
      lat: gps.lat,
      lng: gps.lng,
      serial,
      isCap: getDeviceType(device?.capabilities) === 'CAP',
      connected: device.connected !== false,
    });
  });

  enrichGpsMapForCaps(devices, gpsMap);

  return gpsMap;
}

/** CAPs often lack gps in Firestore — derive a map position from direct children. */
function enrichGpsMapForCaps(devices, gpsMap) {
  if (!Array.isArray(devices)) return;

  devices.forEach((device) => {
    if (getDeviceType(device?.capabilities) !== 'CAP') return;
    const capKey = normalizeSerial(device.serial_number);
    if (!capKey || gpsMap.has(capKey)) return;

    const childPoints = [];
    devices.forEach((child) => {
      if (normalizeSerial(child.parent_serial) !== capKey) return;
      const childEntry = gpsMap.get(normalizeSerial(child.serial_number));
      if (childEntry) childPoints.push(childEntry);
    });

    if (childPoints.length === 0) return;

    const lat = childPoints.reduce((sum, p) => sum + p.lat, 0) / childPoints.length;
    const lng = childPoints.reduce((sum, p) => sum + p.lng, 0) / childPoints.length;
    gpsMap.set(capKey, {
      lat,
      lng,
      serial: device.serial_number,
      isCap: true,
      connected: device.connected !== false,
    });
  });
}

export function expandDeviceFilterSerials(devices, selectedSerials) {
  const expanded = new Set(selectedSerials);
  if (!Array.isArray(devices) || selectedSerials.size === 0) return expanded;

  const devicesBySerial = new Map();
  devices.forEach((device) => {
    if (device?.serial_number) {
      devicesBySerial.set(normalizeSerial(device.serial_number), device);
    }
  });

  selectedSerials.forEach((selected) => {
    const device = devicesBySerial.get(selected);
    if (!device || getDeviceType(device.capabilities) !== 'CAP') return;

    devices.forEach((d) => {
      const serial = normalizeSerial(d.serial_number);
      if (!serial) return;
      if (normalizeSerial(d.parent_serial) === selected) expanded.add(serial);
      if (normalizeSerial(d.cap_serial) === selected) expanded.add(serial);
    });
  });

  return expanded;
}

/** Fan out child endpoints when CAP and children share the same GPS (zero-length lines). */
export function applyLinkVisualSpread(links) {
  if (!Array.isArray(links) || links.length === 0) return links;

  const byParent = new Map();
  links.forEach((link) => {
    const group = byParent.get(link.parentSerial) || [];
    group.push(link);
    byParent.set(link.parentSerial, group);
  });

  byParent.forEach((group) => {
    const coincident = group.filter(
      (link) => link.childLat === link.parentLat && link.childLng === link.parentLng,
    );
    if (coincident.length === 0) return;

    const baseLat = coincident[0].parentLat;
    const baseLng = coincident[0].parentLng;
    const radius = coincident.length === 1 ? 0.00006 : 0.00012;

    coincident.forEach((link, index) => {
      const angle = ((2 * Math.PI) * index) / coincident.length - Math.PI / 2;
      link.childLat = baseLat + radius * Math.cos(angle);
      link.childLng = baseLng + radius * Math.sin(angle);
    });
  });

  return links;
}

function resolveWorstVerdict(staVerdict, apVerdict) {
  if (staVerdict === 'reduce' || apVerdict === 'reduce') return 'reduce';
  if (staVerdict === 'raise' || apVerdict === 'raise') return 'raise';
  if (staVerdict === 'suppressed' || apVerdict === 'suppressed') return 'suppressed';
  if (staVerdict === 'ok' && apVerdict === 'ok') return 'ok';
  return 'missing_data';
}

export function buildMapLinks(links, gpsMap) {
  if (!Array.isArray(links)) return [];

  const result = [];
  links.forEach((link) => {
    const childKey = normalizeSerial(link.child_serial);
    const parentKey = normalizeSerial(link.parent_serial);
    const childGps = gpsMap.get(childKey);
    const parentGps = gpsMap.get(parentKey);
    if (!childGps || !parentGps) return;

    const worstVerdict = resolveWorstVerdict(link.sta_verdict, link.ap_verdict);

    result.push({
      id: `${childKey}:${parentKey}`,
      childSerial: childKey,
      parentSerial: parentKey,
      band: link.band || '',
      childLat: childGps.lat,
      childLng: childGps.lng,
      parentLat: parentGps.lat,
      parentLng: parentGps.lng,
      staTxCurrent: link.sta_tx_current ?? null,
      staTxRecommended: link.sta_tx_recommended ?? null,
      rssiAtParent: link.rssi_at_parent_dbm ?? null,
      staVerdict: link.sta_verdict || 'missing_data',
      apRadioName: link.ap_radio_name || '',
      apTxCurrent: link.ap_tx_current ?? null,
      apTxRecommended: link.ap_tx_recommended ?? null,
      rssiAtChild: link.rssi_at_child_dbm ?? null,
      apVerdict: link.ap_verdict || 'missing_data',
      hasAction: Boolean(link.has_action),
      color: VERDICT_COLORS[worstVerdict] || VERDICT_COLORS.missing_data,
      worstVerdict,
    });
  });

  return result;
}

/** Fallback when the API omits links[] — draw edges from device topology + summary TX data. */
export function buildMapLinksFromTopology(devices, gpsMap, deviceTxPower = []) {
  if (!Array.isArray(devices)) return [];

  const txBySerial = new Map();
  (deviceTxPower || []).forEach((entry) => {
    const serial = normalizeSerial(entry?.serial);
    if (serial) txBySerial.set(serial, entry);
  });

  const result = [];
  devices.forEach((device) => {
    const parentSerial = device?.parent_serial;
    if (!parentSerial) return;

    const childKey = normalizeSerial(device.serial_number);
    const parentKey = normalizeSerial(parentSerial);
    if (!childKey || !parentKey) return;

    const childGps = gpsMap.get(childKey);
    const parentGps = gpsMap.get(parentKey);
    if (!childGps || !parentGps) return;

    const txInfo = txBySerial.get(childKey);
    const downlink = txInfo?.downlink_radios?.[0];

    result.push({
      id: `${childKey}:${parentKey}`,
      childSerial: childKey,
      parentSerial: parentKey,
      band: txInfo?.uplink_band || '',
      childLat: childGps.lat,
      childLng: childGps.lng,
      parentLat: parentGps.lat,
      parentLng: parentGps.lng,
      staTxCurrent: txInfo?.uplink_tx_power ?? null,
      staTxRecommended: null,
      rssiAtParent: txInfo?.uplink_rssi_up ?? null,
      staVerdict: 'ok',
      apRadioName: downlink?.name || '',
      apTxCurrent: downlink?.tx_power ?? null,
      apTxRecommended: null,
      rssiAtChild: txInfo?.uplink_rssi ?? null,
      apVerdict: 'ok',
      hasAction: false,
      color: VERDICT_COLORS.ok,
      worstVerdict: 'ok',
    });
  });

  return result;
}

/** Parse RSSI threshold text field; returns fallback when empty or mid-edit. */
export function parseRssiFilterInput(raw, fallback) {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '' || trimmed === '-') return fallback;
  const v = Number(trimmed);
  return Number.isFinite(v) ? v : fallback;
}

/** True when link has AP-side (child-heard) or STA-side (parent-heard) RSSI below threshold. */
export function linkBelowRssiThresholds(link, minRssiAP, minRssiSTA) {
  const weakApSide = link.rssiAtChild != null && link.rssiAtChild < minRssiAP;
  const weakStaSide = link.rssiAtParent != null && link.rssiAtParent < minRssiSTA;
  return weakApSide || weakStaSide;
}

export function filterLinks(mapLinks, filters, devices) {
  if (!Array.isArray(mapLinks)) return [];
  const {
    showMode = 'all',
    minRssiAP = -75,
    minRssiSTA = -75,
    bandFilter = 'all',
    deviceSerials,
  } = filters || {};

  const hasDeviceFilter = deviceSerials && deviceSerials.size > 0;
  const normalizedDeviceSerials = hasDeviceFilter
    ? new Set(Array.from(deviceSerials).map(normalizeSerial))
    : null;
  const effectiveDeviceSerials = hasDeviceFilter
    ? expandDeviceFilterSerials(devices, normalizedDeviceSerials)
    : null;

  return mapLinks.filter((link) => {
    if (bandFilter && bandFilter !== 'all' && link.band !== bandFilter) return false;

    const childKey = normalizeSerial(link.childSerial);
    const parentKey = normalizeSerial(link.parentSerial);

    if (hasDeviceFilter) {
      const touchesDevice = effectiveDeviceSerials.has(childKey)
        || effectiveDeviceSerials.has(parentKey);
      if (!touchesDevice) return false;
      // Device filter: show every backhaul link involving the selection (uplink + downlinks).
      return true;
    }

    if (showMode === 'actionable') {
      if (!link.hasAction) return false;
    } else if (showMode === 'weak_rssi') {
      if (!linkBelowRssiThresholds(link, minRssiAP, minRssiSTA)) return false;
    }

    return true;
  });
}

export function extractDeviceMarkers(devices, gpsMap, mapLinks) {
  if (!Array.isArray(devices)) return [];

  const linkedSerials = new Set();
  if (Array.isArray(mapLinks)) {
    mapLinks.forEach((link) => {
      linkedSerials.add(link.childSerial);
      linkedSerials.add(link.parentSerial);
    });
  }

  const markers = [];
  gpsMap.forEach((gps, serialKey) => {
    markers.push({
      serial: gps.serial,
      lat: gps.lat,
      lng: gps.lng,
      isCap: gps.isCap,
      connected: gps.connected,
      hasLink: linkedSerials.has(serialKey),
    });
  });

  return markers;
}

export function formatTxChange(current, recommended) {
  if (current == null) return '';
  if (recommended == null || recommended === undefined) return `${current} dBm`;
  return `${current} → ${recommended} dBm`;
}

/** True when the backend marked this action as increasing TX power. */
export function isPowerRaise(action) {
  return action?.adjustment === 'raise';
}

/** Human-readable signed dB change for the actions table. */
export function formatPowerDelta(action) {
  const imp = action?.improvement ?? 0;
  if (isPowerRaise(action)) return `+${imp} dB`;
  return `−${imp} dB`;
}

/** Default tier policy — mirrors backend constants when params_used is absent. */
export const POWER_POLICY_DEFAULTS = {
  operatingTierDbm: -75,
  coverageFloorDbm: -79,
  maxRecommendDbm: 18,
  raiseMaxStepDb: 3,
};

export const VERDICT_LEGEND = [
  { key: 'reduce', label: 'Over-powered (lower)' },
  { key: 'raise', label: 'Under coverage (raise)' },
  { key: 'ok', label: 'OK' },
  { key: 'suppressed', label: 'Suppressed' },
  { key: 'missing_data', label: 'Missing data' },
];

function formatArrowTx(current, recommended) {
  if (current == null) return '—';
  if (recommended != null && recommended !== current) return `${current}→${recommended}`;
  return `${current}`;
}

function formatArrowRssi(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)}`;
}

/** Compact TX + RSSI lines for map arrow labels (child → parent). */
export function formatLinkArrowLines(link) {
  return {
    staLine: `↑ ${formatArrowTx(link.staTxCurrent, link.staTxRecommended)} dBm · ${formatArrowRssi(link.rssiAtParent)} dBm`,
    apLine: `↓ ${formatArrowTx(link.apTxCurrent, link.apTxRecommended)} dBm · ${formatArrowRssi(link.rssiAtChild)} dBm`,
  };
}

export const DEFAULT_POWER_FILTERS = {
  showMode: 'all',
  minRssiAP: -75,
  minRssiSTA: -75,
  bandFilter: 'all',
  deviceSerials: new Set(),
};
